/**
 * Dev Console Access Control
 *
 * Manages the client-side dev console access state.
 * The API at /api/dev-console/access checks:
 * - globalEnabled config
 * - per-user overrides
 * - system admin role (always allowed)
 *
 * This module caches the result and exposes a synchronous
 * check for use in tile visibility functions.
 */

import { useState, useEffect } from 'react';
import { useSessionStore } from './session';
import { isAdminRole } from '../utils/roles';
import { getSessionToken } from '../utils/auth';
import { registerHomeTile, type HomeTile } from './homescreen';

let _devConsoleAllowed = false;
let _devConsoleChecked = false;

/** Synchronous check: is the current user allowed to see the Developer tile? */
export function isDevConsoleAllowed(): boolean {
  // If API has been checked, use that result (respects explicit deny even for admins)
  if (_devConsoleChecked) return _devConsoleAllowed;
  // Before API check completes, admins get optimistic access
  if (isAdminRole(useSessionStore.getState().user?.role)) return true;
  return false;
}

/** Listeners notified when the access check result changes. */
const _listeners = new Set<() => void>();

function _notifyListeners() {
  for (const fn of _listeners) fn();
}

/**
 * Fetch dev console access for the current user from the API.
 * Updates the module-level flag and re-registers the given tile
 * so that the homescreen store triggers a re-render.
 *
 * @param devTile - The developer HomeTile to re-register after the check
 */
export async function checkDevConsoleAccess(devTile?: HomeTile): Promise<void> {
  try {
    const token = getSessionToken();
    if (!token) return;

    const res = await fetch('/api/dev-console/access', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;

    const json = await res.json();
    _devConsoleAllowed = json?.data?.allowed === true;
    _devConsoleChecked = true;

    // Notify React hooks so components re-render with updated access state
    _notifyListeners();

    // Re-register the developer tile to trigger a homescreen store update.
    // This causes sections to change, which makes HomePage re-evaluate visible().
    if (devTile) registerHomeTile(devTile);
  } catch {
    // Silently ignore - tile stays hidden for non-admins on failure
  }
}

/**
 * React hook that checks dev console access on mount and returns whether
 * the current user is allowed to see the floating dev console button.
 * Respects admin config (globalEnabled + per-user overrides).
 */
export function useDevConsoleAllowed(): boolean {
  const role = useSessionStore((s) => s.user?.role);
  const isAdmin = isAdminRole(role);

  const [allowed, setAllowed] = useState(() => {
    // If API has been checked, use that result (respects explicit deny even for admins)
    if (_devConsoleChecked) return _devConsoleAllowed;
    // Before API check completes, admins get optimistic access
    if (isAdmin) return true;
    return false;
  });

  useEffect(() => {
    // Subscribe to future access check results
    const onUpdate = () => setAllowed(_devConsoleChecked && _devConsoleAllowed);
    _listeners.add(onUpdate);

    // Always check the API (even for admins - they may be explicitly denied)
    if (!_devConsoleChecked) {
      checkDevConsoleAccess();
    } else {
      setAllowed(_devConsoleAllowed);
    }

    return () => { _listeners.delete(onUpdate); };
  }, [isAdmin]);

  return allowed;
}
